import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  MenuItem,
  Chip,
  Autocomplete,
  TextField,
  Typography,
  Box,
  Alert,
  Slider
} from "@mui/material";

import {
  useForm
} from "react-hook-form";

import {
  getEmployees
} from "../../services/employeeService";

import {
  getProjects
} from "../../services/projectService";

import ResourceCalendar from "./ResourceCalendar";

import CapacityCalendar from "./CapacityCalendar";

// Match the query against the full name or any individual word's start (e.g.
// "Shah" matches "Bhumit Shah"), normalizing whitespace/case for reliability
// against synced data that may contain stray double-spaces.
const normalizeName = (value) => (value || "").toLowerCase().replace(/\s+/g, " ").trim();

const filterEmployeesByName = (options, query) => {
  const normalizedQuery = normalizeName(query);

  if (!normalizedQuery) {
    return options;
  }

  return options.filter((option) => {
    const name = normalizeName(option.employee_name);
    return name.startsWith(normalizedQuery) || name.split(" ").some((word) => word.startsWith(normalizedQuery));
  });
};

const filterEmployeeOptions = (options, state) => filterEmployeesByName(options, state.inputValue);

function ProjectForm({
  project,
  onSubmit
}) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm();

  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employeeCapacities, setEmployeeCapacities] = useState({});
  const [projectManagerId, setProjectManagerId] = useState(project?.project_manager_id || "");
  const [fullyBookedDates, setFullyBookedDates] = useState([]);
  const [dateConflicts, setDateConflicts] = useState([]);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");

  const capacity = watch("capacity_percent", 0);
  const startDate = watch("start_date", project?.start_date || "");
  const endDate = watch("end_date", project?.end_date || "");
  const projectType = watch("project_type", project?.project_type || "Internal");

  const sortedEmployees = useMemo(
    () => [...employees].sort((a, b) => a.employee_name.localeCompare(b.employee_name)),
    [employees]
  );

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const data = await getEmployees();
        setEmployees(data);
      }
      catch (error) {
        console.error("Employee Error:", error);
      }
    };

    loadEmployees();

    if (project) {
      const ids = Array.isArray(project.assigned_to)
        ? project.assigned_to.map((a) => (typeof a === "object" ? a.employee_id : a))
        : (project.assigned_to ? [project.assigned_to] : []);
      const caps = {};
      if (Array.isArray(project.assigned_to)) {
        project.assigned_to.forEach((a) => {
          if (typeof a === "object") caps[a.employee_id] = a.capacity_percent || 0;
        });
      }
      reset({
        project_name: project.project_name,
        description: project.description || "",
        assigned_to: ids,
        start_date: project.start_date || "",
        end_date: project.end_date || "",
        project_type: project.project_type || "Internal",
        client_name: project.client_name || "",
        project_manager_id: project.project_manager_id || "",
      });
      setSelectedEmployeeIds(ids);
      setEmployeeCapacities(caps);
      setProjectManagerId(project.project_manager_id || "");
    }
    else {
      reset({
        project_name: "",
        description: "",
        assigned_to: [],
        start_date: "",
        end_date: "",
        project_type: "Internal",
        client_name: "",
        project_manager_id: "",
      });
      setEmployeeCapacities({});
      setProjectManagerId("");
    }
  }, [project, reset]);

  const handleEmployeeChange = async (ids) => {
    setSelectedEmployeeIds(ids);
    setValue("assigned_to", ids, { shouldValidate: true });
    setDateConflicts([]);

    // Track dates where an employee is already fully committed (300%) across
    // other projects, purely as an informational warning (not a hard block) -
    // employees can legitimately be double/triple booked.
    const allBooked = [];
    for (const employeeId of ids) {
      try {
        const projects = await getProjects("", employeeId);
        projects.forEach((proj) => {
          if (proj.capacity_percent >= 300 && proj.start_date && proj.end_date && proj.project_id !== project?.project_id) {
            const start = new Date(proj.start_date);
            const end = new Date(proj.end_date);
            let current = new Date(start);
            while (current <= end) {
              const dateStr = current.toISOString().split("T")[0];
              if (!allBooked.includes(dateStr)) allBooked.push(dateStr);
              current.setDate(current.getDate() + 1);
            }
          }
        });
      } catch (error) {
        console.error("Error loading capacity dates:", error);
      }
    }
    setFullyBookedDates(allBooked);
  };

  useEffect(() => {
    if (startDate && endDate && fullyBookedDates.length > 0) {
      const conflicts = [];
      const start = new Date(startDate);
      const end = new Date(endDate);
      let current = new Date(start);

      while (current <= end) {
        const dateStr = current.toISOString().split("T")[0];
        if (fullyBookedDates.includes(dateStr)) {
          conflicts.push(dateStr);
        }
        current.setDate(current.getDate() + 1);
      }

      setDateConflicts(conflicts);
    } else {
      setDateConflicts([]);
    }
  }, [startDate, endDate, fullyBookedDates]);

  return (
    <form
      id="project-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit((data) => {
          const payload = {
            ...data,
            assigned_to: selectedEmployeeIds.map((id) => ({
              employee_id: id,
              capacity_percent: employeeCapacities[id] || 0,
            })),
            start_date: data.start_date || null,
            end_date: data.end_date || null,
            project_manager_id: projectManagerId ? Number(projectManagerId) : null,
            client_name: data.project_type === "Client" ? data.client_name : null,
          };
          onSubmit(payload);
        })(e);
      }}
    >
      {/* hidden field so react-hook-form tracks and validates assigned_to */}
      <input type="hidden" {...register("assigned_to", { validate: v => (Array.isArray(v) && v.length > 0) || "required" })} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.2
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            color: "#475569",
            fontWeight: 600,
            mb: 0.2
          }}
        >
          Project Information
        </Typography>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Project Name <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Enter project name"
            error={Boolean(errors.project_name)}
            helperText={errors.project_name ? "Project name is required" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("project_name", { required: true })}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Project Type <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
          </Typography>

          <TextField
            select
            fullWidth
            size="small"
            defaultValue={project?.project_type || "Internal"}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              }
            }}
            {...register("project_type")}
          >
            <MenuItem value="Internal">Internal</MenuItem>
            <MenuItem value="Client">Client</MenuItem>
          </TextField>
        </Box>

        {projectType === "Client" && (
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "#334155",
                mb: 0.8
              }}
            >
              Client Name
            </Typography>

            <TextField
              fullWidth
              size="small"
              placeholder="Enter client name"
              error={Boolean(errors.client_name)}
              helperText={errors.client_name ? "Client name is required" : ""}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  background: "#ffffff"
                },
                "& .MuiFormHelperText-root": {
                  minHeight: 20,
                  mt: 0.7
                }
              }}
              {...register("client_name", { required: projectType === "Client" })}
            />
          </Box>
        )}

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Project Manager / Reporting To <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
          </Typography>

          <Autocomplete
            size="small"
            options={sortedEmployees}
            getOptionLabel={(option) => option.employee_name}
            filterOptions={filterEmployeeOptions}
            isOptionEqualToValue={(option, value) => option.employee_id === value.employee_id}
            value={sortedEmployees.find((emp) => emp.employee_id === projectManagerId) || null}
            onChange={(event, newValue) => setProjectManagerId(newValue ? newValue.employee_id : "")}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search and select project manager"
              />
            )}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#ffffff" } }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Description
          </Typography>

          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Enter project description"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              }
            }}
            {...register("description")}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Assign To <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
          </Typography>

          <Autocomplete
            multiple
            size="small"
            filterSelectedOptions
            options={sortedEmployees}
            getOptionLabel={(option) => option.employee_name}
            filterOptions={filterEmployeeOptions}
            isOptionEqualToValue={(option, value) => option.employee_id === value.employee_id}
            value={sortedEmployees.filter((emp) => selectedEmployeeIds.includes(emp.employee_id))}
            onChange={(event, newValue) => {
              handleEmployeeChange(newValue.map((emp) => emp.employee_id));
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.employee_name}
                    size="small"
                    sx={{ borderRadius: 1 }}
                    {...tagProps}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedEmployeeIds.length === 0 ? "Search and select employees" : ""}
                error={Boolean(errors.assigned_to)}
                helperText={errors.assigned_to ? "Please assign at least one employee" : ""}
              />
            )}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#ffffff" } }}
          />
        </Box>

        {selectedEmployeeIds.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                color: "#475569",
                fontWeight: 600,
                mb: 1.5
              }}
            >
              Current Capacity & Calendar
            </Typography>
            {selectedEmployeeIds.map((empId) => {
              const emp = employees.find((e) => e.employee_id === empId);
              return (
                <Box
                  key={empId}
                  mb={3}
                  sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0", background: "#fafbfc" }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#334155", mb: 1.5 }}>
                    {emp ? emp.employee_name : `Employee ${empId}`}
                  </Typography>

                  <Box mb={2}>
                    <ResourceCalendar employeeId={empId} />
                  </Box>

                  <CapacityCalendar employeeId={empId} />
                </Box>
              );
            })}
          </Box>
        )}

        <Typography
          sx={{ fontSize: 13, color: "#475569", fontWeight: 600, mt: 2, mb: 0.2 }}
        >
          Resource Allocation
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            "& .react-datepicker-wrapper": { width: "100%" }
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 1.5 }}>
              Project Start Date
            </Typography>

            <DatePicker
              selected={startDate ? new Date(startDate) : null}
              onChange={(date) => {
                setValue("start_date", date ? date.toISOString().split("T")[0] : "", { shouldValidate: true });
                setStartDateError("");
              }}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select start date"
              popperPlacement="bottom-start"
              popperProps={{ strategy: "fixed" }}
              customInput={
                <TextField
                  fullWidth
                  size="small"
                  error={Boolean(startDateError)}
                  helperText={startDateError || " "}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#ffffff" } }}
                />
              }
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 1.5 }}>
              Project End Date
            </Typography>

            <DatePicker
              selected={endDate ? new Date(endDate) : null}
              onChange={(date) => {
                setValue("end_date", date ? date.toISOString().split("T")[0] : "", { shouldValidate: true });
                setEndDateError("");
              }}
              minDate={startDate ? new Date(startDate) : null}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select end date"
              popperPlacement="bottom-end"
              popperProps={{ strategy: "fixed" }}
              customInput={
                <TextField
                  fullWidth
                  size="small"
                  error={Boolean(endDateError)}
                  helperText={endDateError || " "}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#ffffff" } }}
                />
              }
            />
          </Box>
        </Box>

        {dateConflicts.length > 0 && (
          <Alert severity="warning">
            ⚠️ Employee is already fully committed (300%) on {dateConflicts.length} date(s) in this range:
            <br />
            {dateConflicts.slice(0, 5).join(", ")}
            {dateConflicts.length > 5 && ` and ${dateConflicts.length - 5} more`}
          </Alert>
        )}

        {selectedEmployeeIds.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 1.5 }}>
              Capacity Allocation
            </Typography>
            {selectedEmployeeIds.map((empId) => {
              const emp = employees.find((e) => e.employee_id === empId);
              const cap = employeeCapacities[empId] || 0;
              return (
                <Box key={empId} mb={2.5} sx={{ px: 2, py: 1.5, borderRadius: 2, background: "#f1f5f9" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                      {emp ? emp.employee_name : `Employee ${empId}`}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: cap >= 200 ? "#ef4444" : cap > 100 ? "#f97316" : "#22c55e" }}>
                      {cap}%
                    </Typography>
                  </Box>
                  <Slider
                    value={cap}
                    onChange={(e, val) => setEmployeeCapacities((prev) => ({ ...prev, [empId]: val }))}
                    min={0} max={300} step={5}
                    marks={[{ value: 0, label: "0%" }, { value: 100, label: "100%" }, { value: 200, label: "200%" }, { value: 300, label: "300%" }]}
                    valueLabelDisplay="auto"
                    sx={{
                      "& .MuiSlider-thumb": { backgroundColor: cap >= 200 ? "#ef4444" : cap > 100 ? "#f97316" : "#22c55e" },
                      "& .MuiSlider-track": { backgroundColor: cap >= 200 ? "#ef4444" : cap > 100 ? "#f97316" : "#22c55e" }
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </form>
  );
}

export default ProjectForm;
